// Static SEO metadata for genre landing pages at /beat-battles/$genre.
// The slug must match a system-genre slug used by the API for /feed?genre=.

export type GenreLanding = {
  slug: string;
  name: string;
  bpmRange: string;
  description: string;
  intro: string;
  signature: string[];
  rules: string[];
  faqs: { q: string; a: string }[];
};

export const GENRE_LANDINGS: GenreLanding[] = [
  {
    slug: 'trap',
    name: 'Trap',
    bpmRange: '130-160 BPM',
    description:
      'Compete in trap beat battles. 808s, hi-hat triplets, vocal chops. Ranked per genre, weekly tournaments, free to play.',
    intro:
      'Trap is the most-played genre on Prod Battle. Pocket-locked 808s, half-time grooves, and snappy snares define the sound. Every match drops a fresh trap sample kit and you have a window to ship a beat.',
    signature: [
      '808 sub-bass with portamento glide',
      'Hi-hat triplet rolls and stutters',
      'Snare or clap on beat 3, kick on 1 and 3',
      'Vocal chops and pitched one-shots',
      'Sparse melodic loops in minor keys',
    ],
    rules: [
      'BPM range 130 to 160',
      'Originality required - no pre-made beats',
      '808 is mandatory; full hooks from existing tracks are not',
      'Submission length 60 to 120 seconds',
    ],
    faqs: [
      {
        q: 'What BPM is a trap beat battle?',
        a: 'Most trap battles run at 140 BPM with a half-time feel. The matchmaker enforces 130 to 160 BPM.',
      },
      {
        q: 'Do I need 808 plugins to compete?',
        a: 'No. Every match drops a sample kit including 808 one-shots. Stock DAW synths can also produce battle-ready 808s.',
      },
      {
        q: 'Can I submit a beat I already made?',
        a: 'No. Beats must be made during the round. Spectral fingerprinting catches re-uploads.',
      },
    ],
  },
  {
    slug: 'house',
    name: 'House',
    bpmRange: '120-128 BPM',
    description:
      'Compete in house beat battles. Four-on-the-floor, off-beat hats, deep basslines. Real-time matches at 120-128 BPM.',
    intro:
      'House battles run on a strict four-on-the-floor backbone. The fun is in what you do around the kick: off-beat hats, sliding basslines, filtered chord stabs, and a build that earns its drop.',
    signature: [
      'Kick on every beat (four-on-the-floor)',
      'Hi-hats on the off-beat (the "&")',
      'Bassline locked to the kick rhythm',
      'Filtered chord stabs or organ chords',
      'Builds with rising synths and snare rolls',
    ],
    rules: [
      'BPM range 120 to 128',
      'Originality required',
      'Submission length 60 to 120 seconds',
      'Four-on-the-floor pattern expected (variations encouraged)',
    ],
    faqs: [
      {
        q: 'What BPM is a house beat battle?',
        a: '120 to 128 BPM. 124 BPM is the most common.',
      },
      {
        q: 'Tech house, deep house, or generic house?',
        a: 'Generic house. Tech house and deep house get their own subgenre matches when there is enough demand.',
      },
    ],
  },
  {
    slug: 'dnb',
    name: 'Drum and Bass',
    bpmRange: '170-176 BPM',
    description:
      'Compete in drum and bass beat battles. Breakbeats, sub-bass, rolling drum patterns. 170-176 BPM matches.',
    intro:
      'Drum and bass battles reward producers who can program a tight breakbeat and design a bass that moves. The BPM is fixed at 174 give or take, the energy is constant, and the listening voters know exactly what they expect.',
    signature: [
      'Two-step or Amen-style breakbeat at 174 BPM',
      'Reese bass with movement and width',
      'Sub-bass under the Reese',
      'Ride and sizzle layers on the breaks',
      'Intro-drop-breakdown-drop arrangement',
    ],
    rules: [
      'BPM range 170 to 176',
      'Sub-bass required',
      'Originality required',
      'Submission length 60 to 120 seconds',
    ],
    faqs: [
      {
        q: 'What BPM is a drum and bass beat battle?',
        a: '174 BPM is standard. The matchmaker accepts 170 to 176.',
      },
      {
        q: 'Liquid, neuro, or jump-up?',
        a: 'Generic DnB matches accept all subgenres. Specific subgenre tournaments run periodically.',
      },
    ],
  },
  {
    slug: 'hip-hop',
    name: 'Hip-Hop',
    bpmRange: '80-95 BPM',
    description:
      'Compete in hip-hop beat battles. Boom-bap drums, sample flips, classic groove at 80-95 BPM. Ranked per genre, free to play.',
    intro:
      'Hip-hop battles are the original beat-battle format. Tight drums, a sampled-sounding melody, and a pocket that nods. The BPM is slower than trap and the swing matters more than the loudness.',
    signature: [
      'Boom-bap drum patterns - kick on 1, snare on 2 and 4',
      'Sample-flip melodies (or original chops)',
      'Light swing on hi-hats',
      'Bass on root notes, not pitched runs',
      'Less compression than trap; more breathing room',
    ],
    rules: [
      'BPM range 80 to 95',
      'Originality required',
      'No copyrighted samples (kit one-shots are fine)',
      'Submission length 60 to 120 seconds',
    ],
    faqs: [
      {
        q: 'What BPM is a hip-hop beat battle?',
        a: '80 to 95 BPM. 90 is the most common.',
      },
      {
        q: 'Can I sample existing songs?',
        a: 'No. Every kit drop includes one-shot melodics you can chop. Copyrighted samples are flagged automatically.',
      },
    ],
  },
  {
    slug: 'lo-fi',
    name: 'Lo-Fi',
    bpmRange: '70-90 BPM',
    description:
      'Compete in lo-fi beat battles. Saturated drums, jazzy chords, dust and warmth. 70-90 BPM, sample-flip friendly.',
    intro:
      'Lo-fi battles are about texture as much as composition. Voters listen for warmth, vinyl crackle, jazzy chord motion, and the right amount of imperfection. Clean and clinical loses to hand-played and dusty.',
    signature: [
      'Slow tempo, swung drums',
      'Saturated, lo-fi-filtered drums',
      'Jazzy 7th and 9th chords',
      'Tape hiss and vinyl crackle layers',
      'Sample-flip-friendly arrangement',
    ],
    rules: [
      'BPM range 70 to 90',
      'Originality required',
      'Submission length 60 to 120 seconds',
      'Saturation/dust strongly encouraged but not required',
    ],
    faqs: [
      {
        q: 'What BPM is a lo-fi beat battle?',
        a: '70 to 90 BPM. 80-85 is the sweet spot.',
      },
    ],
  },
  {
    slug: 'phonk',
    name: 'Phonk',
    bpmRange: '130-150 BPM',
    description:
      'Compete in phonk beat battles. Distorted 808s, cowbell, Memphis-style vocal chops. 130-150 BPM ranked matches.',
    intro:
      'Phonk battles are the crunchiest matches on the platform. Distorted 808s, cowbell on the and-of-3, and Memphis vocal chops define the sound. The mix is intentionally rough - too clean reads as wrong genre.',
    signature: [
      'Distorted 808 sub-bass',
      'Cowbell on the and-of-3',
      'Memphis-style vocal one-shots and pitched chops',
      'Heavy saturation on the master',
      'Driving half-time grooves',
    ],
    rules: [
      'BPM range 130 to 150',
      'Distortion encouraged',
      'Submission length 60 to 120 seconds',
      'Originality required',
    ],
    faqs: [
      {
        q: 'What BPM is a phonk beat battle?',
        a: '130 to 150 BPM. 140 is most common.',
      },
      {
        q: 'Drift phonk or Memphis phonk?',
        a: 'Both accepted. Drift phonk submissions tend to win on energy; Memphis on character.',
      },
    ],
  },
];

export const GENRE_LANDING_BY_SLUG: Record<string, GenreLanding> = Object.fromEntries(
  GENRE_LANDINGS.map((g) => [g.slug, g]),
);
